import { useEffect, useState, type ReactElement } from 'react';
import { Navigate } from 'react-router-dom';
import { Box, Button, Tabs } from '@mantine/core';
import { KeyRound, Lightbulb, RefreshCw, Table2 } from 'lucide-react';
import { useAuth } from '@/features/auth/AuthContext';
import PageHeaderComponent from '@/design/PageHeaderComponent';
import PageLevelFilterRow from '@/shared/surfaces/PageLevelFilterRow';
import SideRailPageLayout from '@/shared/surfaces/SideRailPageLayout';
import TopicSummaryPanel from '@/shared/surfaces/TopicSummaryPanel';
import type { StatItem } from '@/design/StatBand';
import { useViewModel } from '@/shared/viewmodels/useViewModel';
import { LicensesPageViewModel } from './LicensesPageViewModel';
import LicenseSummaryPanel from './LicenseSummaryPanel';
import ReassignmentPanel from './ReassignmentPanel';

function seatStats(vm: LicensesPageViewModel): StatItem[] {
    return [
        { label: 'Pool', value: vm.totalPool.toLocaleString('en-US') },
        { label: 'Idle', value: vm.hasSeatActivityData ? vm.totalIdle.toLocaleString('en-US') : '—', hint: 'assigned but inactive' },
        { label: 'Available', value: vm.totalAvailable.toLocaleString('en-US'), hint: 'unassigned seats' },
    ];
}

/**
 * The Licenses landing page, served at `/licenses`: the stylized header (with the
 * Sync all action), a main column tabbed between the pool inventory (Overview) and
 * the reassignment Recommendations, beside a side rail with the seat summary
 * (Pool · Idle · Available). The page owns the single {@link LicensesPageViewModel}
 * instance and initializes it once. Hard-dollar cost lives in Spend › Cost. The
 * route gates on `view licenses`; this redirect is defense-in-depth.
 */
export default function LicensesPage(): ReactElement {
    const { hasPermission } = useAuth();
    const vm = useViewModel(LicensesPageViewModel);
    const [tab, setTab] = useState<string | null>('overview');

    useEffect(() => {
        void vm.initialize();
    }, [vm]);

    if (!hasPermission({ action: 'view', resource: 'licenses' })) return <Navigate to="/insights" replace />;

    return (
        <Box pb="xl">
            <PageLevelFilterRow />

            <PageHeaderComponent
                icon={KeyRound}
                eyebrow="Licenses"
                title="Licenses"
                subtitle="Seat inventory, assignment, and reclamation across every connected AI provider."
                rightToolbar={
                    <Button size="xs" variant="default" leftSection={<RefreshCw size={14} />} onClick={() => void vm.syncAll()} loading={vm.syncing} disabled={vm.syncing}>
                        Sync all
                    </Button>
                }
            />

            <SideRailPageLayout rail={<TopicSummaryPanel stats={seatStats(vm)} />}>
                <Tabs value={tab} onChange={setTab} keepMounted={false}>
                    <Tabs.List mb="lg">
                        <Tabs.Tab value="overview" leftSection={<Table2 size={14} />}>
                            Overview
                        </Tabs.Tab>
                        <Tabs.Tab value="recommendations" leftSection={<Lightbulb size={14} />}>
                            Recommendations
                        </Tabs.Tab>
                    </Tabs.List>

                    <Tabs.Panel value="overview">
                        <LicenseSummaryPanel vm={vm} />
                    </Tabs.Panel>
                    <Tabs.Panel value="recommendations">
                        <ReassignmentPanel recommendations={vm.recommendations} estReclaimedMonthly={vm.estReclaimedMonthly} spendCurrency={vm.spendCurrency} hasSpendData={vm.hasSpendData} />
                    </Tabs.Panel>
                </Tabs>
            </SideRailPageLayout>
        </Box>
    );
}
